import { DollarSign, Eye, MousePointerClick, Users, ShoppingCart, Target, TrendingUp, Award } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export function DashboardPreview() {
  const { t } = useI18n();

  const metrics = [
    { icon: DollarSign, labelKey: "dashboard.metric.spend", value: "$12,840", change: "+12%" },
    { icon: Eye, labelKey: "dashboard.metric.reach", value: "284,920", change: "+34%" },
    { icon: MousePointerClick, labelKey: "dashboard.metric.clicks", value: "8,412", change: "+22%" },
    { icon: Users, labelKey: "dashboard.metric.leads", value: "1,238", change: "+48%" },
    { icon: ShoppingCart, labelKey: "dashboard.metric.sales", value: "342", change: "+19%" },
    { icon: Target, labelKey: "dashboard.metric.cpl", value: "$10.37", change: "-23%" },
    { icon: TrendingUp, labelKey: "dashboard.metric.roas", value: "4.8x", change: "+15%" },
    { icon: Award, labelKey: "dashboard.metric.topCampaign", valueKey: "dashboard.metric.topCampaignValue", change: "Live" },
  ];

  return (
    <section className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">{t("dashboard.title")} <span className="text-gradient-orange">{t("dashboard.titleHighlight")}</span></h2>
          <p className="text-sm italic mt-2" style={{ color: "#94a3b8" }}>{t("dashboard.sampleNote")}</p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mt-3">{t("dashboard.desc")}</p>
        </div>

        <div className="relative rounded-3xl border border-border bg-gradient-to-br from-card to-surface p-2 shadow-2xl">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 rounded-3xl blur-2xl opacity-50" />
          <div className="relative rounded-[20px] bg-background p-6 md:p-8">
            <div className="flex items-center justify-between mb-6 pb-5 border-b border-border">
              <div>
                <h3 className="text-lg font-bold">{t("dashboard.cardTitle")}</h3>
                <p className="text-xs text-muted-foreground">{t("dashboard.cardPeriod")}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-muted-foreground">{t("dashboard.cardLive")}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.map(({ icon: Icon, labelKey, value, valueKey, change }) => {
                const positive = change.startsWith('+') || change === 'Live';
                return (
                  <div key={labelKey} className="p-4 rounded-2xl bg-card border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className={`text-xs font-medium ${positive ? 'text-green-400' : 'text-red-400'}`}>{change}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{t(labelKey)}</p>
                    <p className="text-xl font-bold">{valueKey ? t(valueKey) : value}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 h-32 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 border border-border flex items-end p-4 gap-2">
              {[40, 65, 50, 75, 60, 85, 70, 90, 78, 95, 82, 100].map((h, i) => (
                <div key={i} className="flex-1 bg-gradient-to-t from-primary to-secondary rounded-t" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>

        <p className="text-center text-muted-foreground mt-8 max-w-2xl mx-auto">
          {t("dashboard.footer")}
        </p>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
          <div>
            <p className="text-base md:text-lg font-semibold text-gradient-orange">{t("dashboard.stat1")}</p>
          </div>
          <div>
            <p className="text-base md:text-lg font-semibold text-gradient-orange">{t("dashboard.stat2")}</p>
          </div>
          <div>
            <p className="text-base md:text-lg font-semibold text-gradient-orange">{t("dashboard.stat3")}</p>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-gradient-orange mb-1">24/7</div>
            <p className="text-xs text-muted-foreground">{t("dashboard.stat4Suffix")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
